import { CalendarCheckIcon, CheckCircleIcon, ProhibitIcon } from '@phosphor-icons/react'
import type { BadgeColor } from 'app/components/ui/badge'
import { ListBoardExit } from 'app/components/lists/board/ListBoardExit'
import { BoardColumn } from './boardColumnSource'
import { ExitStageCount, useExitStageCount } from './ExitStage'

const EXIT_LOOKS = {
  booked: {
    Icon: CalendarCheckIcon,
    badge: `green`,
    armed: `border-green-500 bg-green-50/70 text-green-900`,
    over: `border-solid border-green-600 bg-green-100 text-green-900 shadow-elevation-2`,
  },
  done: {
    Icon: CheckCircleIcon,
    badge: `green`,
    armed: `border-green-500 bg-green-50/70 text-green-900`,
    over: `border-solid border-green-600 bg-green-100 text-green-900 shadow-elevation-2`,
  },
  lost: {
    Icon: ProhibitIcon,
    // Grey, not red. A case the practice lost is a fact to record, not an
    // error to alarm anybody about, and the count sits on a resting board.
    badge: `gray`,
    armed: `border-red-300 bg-red-50/70 text-red-900`,
    over: `border-solid border-red-500 bg-red-100 text-red-900 shadow-elevation-2`,
  },
} as const satisfies Record<string, { badge: BadgeColor; [key: string]: unknown }>

/**
 * Matching on the label is safe: every exit stage on a board comes from
 * `systemPipelines.ts`, whose whole vocabulary is Scheduled, Completed, Lost
 * and Closed lost.
 */
function exitLook(label: string) {
  if (/lost|declin/i.test(label)) return EXIT_LOOKS.lost
  if (/complet/i.test(label)) return EXIT_LOOKS.done
  return EXIT_LOOKS.booked
}

export function ExitDropZone({
  column,
  draggingId,
  pipelineId,
  writes,
  onDrop,
  onOpenList,
  touchOver,
}: {
  column: BoardColumn
  draggingId?: string
  /** Which pipeline's stage this exit writes, for counting who is already in it. */
  pipelineId?: string
  /** Bumped by the board on every move that lands, so the count re-reads. */
  writes?: number
  onDrop: (contactId: string) => void
  onOpenList: () => void
  touchOver?: boolean
}) {
  // A drag is happening somewhere on the board, though not necessarily here.
  const armed = !!draggingId
  const look = exitLook(column.label)
  const count = useExitStageCount(pipelineId, column.key, writes)
  // The zone is a doorway as well as a drop target: pressing it lists everyone
  // at this stage. Openable at zero too, since a case dropped a second ago is
  // exactly what somebody presses this to go and find.
  const openable = !!pipelineId
  return (
    <ListBoardExit
      dropKey={column.key}
      icon={look.Icon}
      resting={`Drag here for ${column.label.toLowerCase()}`}
      dropping={`Drop to mark ${column.label.toLowerCase()}`}
      armedClass={look.armed}
      overClass={look.over}
      armed={armed}
      touchOver={touchOver}
      fallbackId={draggingId}
      onDrop={onDrop}
      onOpenList={openable ? onOpenList : undefined}
      title={openable ? `Show ${count ? `the ${count} ` : `everyone `}at ${column.label.toLowerCase()}` : undefined}
    >
      {/* A card dropped here leaves the queue's own search, so without this
          count the people a practice booked are gone from the page that booked
          them. The number itself is absent at zero; the way in is not. */}
      {openable && <ExitStageCount count={count} label={column.label} color={look.badge} />}
    </ListBoardExit>
  )
}
